import React, { useEffect, useRef, useState } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FlexBox, FlexBoxBetween } from '../../../../components/tableItems';
import { MainButton } from '../../../../components/buttons';
import { filtersAliasDefault } from '../../../../models/Alias';
import useAliasStore from '../../../../stores/alias/useAliasStore';
import { Owners } from '../../../../models/User';
import AutoCompleteSelect from '../../../../components/Filters/AutoCompleteSelect';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '../../../../components/Accordion/Accordion';
import { DefaultCheckbox } from '../../../../components/inputs';

interface AliasFilterInterface {
  updateDrawerFilter: () => void;
  users: Owners[];
  sites: { id: number; name: string }[];
}

const AliasFilter: React.FC<AliasFilterInterface> = ({
  updateDrawerFilter,
  users,
  sites,
}) => {
  const isFirstRender = useRef(true);
  const {
    filterValuesChange,
    filterValuesDefault,
    updateFiltersChange,
    updateFiltersDefault,
    updateAliasRequest,
    loadingAlias,
  } = useAliasStore();

  const [filtersKey, setFiltersKey] = useState(0);

  const createQuery = () => {
    const copy = { ...filterValuesDefault };

    const copyFilters = JSON.parse(JSON.stringify(copy));

    const ArrFilters: { id: string; value: string }[] = Object.assign(
      [],
      Object.values(copyFilters),
    );
    const arrayElements = ArrFilters.filter(item => item.value);
    const queryString = arrayElements.map(
      query => `&${query.id}=${query.value}`,
    );

    updateAliasRequest({
      page: 1,
      filters: queryString.join(''),
    });
  };

  useEffect(() => {
    createQuery();
  }, [filterValuesDefault]);

  useEffect(() => {
    return () => {
      updateFiltersChange(filtersAliasDefault);
      updateFiltersDefault(filtersAliasDefault);
    };
  }, []);

  const onEnterOption = () => updateFiltersDefault(filterValuesDefault);

  const filtersArray = [
    {
      id: 1,
      title: 'Owner',
      countUse:
        filterValuesChange.user.value !== ''
          ? filterValuesChange.user.value.split(',').length
          : 0,
      body: (
        <AutoCompleteSelect
          disabledInput={loadingAlias}
          options={users.map(item => {
            return { id: item.id, name: item.username };
          })}
          clearAll={false}
          onEnter={onEnterOption}
          placeholder="User"
          updateFiltersValue={(id: string, value: string) => {
            updateFiltersChange({ user: { id: 'user', value } });
          }}
          value={users
            .map(item => {
              return { id: item.id, name: item.username };
            })
            .filter(item => {
              const id = item.id as number;
              return (
                filterValuesChange.user.value
                  .split(',')
                  .indexOf(id.toString()) !== -1
              );
            })}
          id={filterValuesChange.user.id}
        />
      ),
      disabled: !(users.length > 0),
    },
    {
      id: 2,
      countUse: filterValuesChange.is_active.value !== '' ? 1 : 0,
      title: 'Active',
      body: (
        <FlexBox>
          <DefaultCheckbox
            size="small"
            onClick={() => {
              updateFiltersChange({
                is_active: {
                  id: 'is_active',
                  value:
                    filterValuesChange.is_active.value === '' ? 'true' : '',
                },
              });
            }}
            checked={filterValuesChange.is_active.value !== ''}
          />
          <Typography fontSize={12}>Is Active</Typography>
        </FlexBox>
      ),
      disabled: false,
    },

    {
      id: 4,
      title: 'Site',
      countUse:
        filterValuesChange.site.value !== ''
          ? filterValuesChange.site.value.split(',').length
          : 0,
      body: (
        <AutoCompleteSelect
          disabledInput={loadingAlias}
          options={sites}
          clearAll={false}
          onEnter={onEnterOption}
          placeholder="Site"
          updateFiltersValue={(id: string, value: string) => {
            updateFiltersChange({ site: { id: 'site', value } });
          }}
          value={sites.filter(item => {
            const id = item.id as number;
            return (
              filterValuesChange.site.value
                .split(',')
                .indexOf(id.toString()) !== -1
            );
          })}
          id={filterValuesChange.site.id}
        />
      ),
      disabled: !(sites.length > 0),
    },
  ];

  return (
    <Box mt={2}>
      <FlexBoxBetween px={3}>
        <Typography sx={{ fontWeight: 900, fontSize: 16 }}>Filters</Typography>
        <IconButton
          aria-label="close"
          disabled={false}
          id="long-button"
          aria-haspopup="true"
          onClick={() => updateDrawerFilter()}
        >
          <CloseIcon
            sx={{
              color: 'black',
              '&:hover': { color: 'red' },
            }}
          />
        </IconButton>
      </FlexBoxBetween>
      <FlexBoxBetween mt={1} px={3}>
        <MainButton
          disabled={false}
          size="small"
          variant="contained"
          type="submit"
          onClick={() => {
            updateFiltersDefault(filterValuesChange);
            updateDrawerFilter();
          }}
        >
          Apply Filters
        </MainButton>
        <Typography
          onClick={() => {
            if (loadingAlias) return;
            updateFiltersChange(filtersAliasDefault);
            updateFiltersDefault(filtersAliasDefault);
            updateDrawerFilter();
            setFiltersKey(prevKey => prevKey + 1);
          }}
          sx={{
            fontSize: 14,
            cursor: 'pointer',
            ':hover': {
              color: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          Clear All
        </Typography>
      </FlexBoxBetween>
      <Box marginTop={1.5}>
        <Divider sx={{ border: '1px solid rgba(0, 0, 0, 0.06)' }} />

        <div key={filtersKey}>
          {filtersArray.map(filter => (
            <Box position="relative" key={filter.id}>
              <Accordion>
                <AccordionSummary
                  disabled={filter.disabled}
                  aria-controls={`${filter.title}-content`}
                  id={`${filter.title}-header`}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <FlexBox>
                      <Box
                        sx={{
                          backgroundColor:
                            filter.countUse !== undefined && filter.countUse > 0
                              ? '#CEFFBA'
                              : '',
                        }}
                      >
                        <Typography
                          textTransform="capitalize"
                          fontSize={12}
                          color="#000000"
                          fontWeight={700}
                        >
                          {filter.title}
                        </Typography>
                      </Box>
                      {filter.countUse !== undefined && filter.countUse > 0 && (
                        <Typography
                          textTransform="capitalize"
                          fontSize={12}
                          color="#000000"
                          marginLeft={1.5}
                          fontWeight={400}
                          sx={{
                            backgroundColor: '#E2E2E2',
                            padding: '2px 4px',
                            borderRadius: '2px',
                          }}
                        >
                          {filter.countUse}
                        </Typography>
                      )}
                    </FlexBox>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>{filter.body}</AccordionDetails>
              </Accordion>
            </Box>
          ))}
        </div>
      </Box>
    </Box>
  );
};

export default AliasFilter;
